import { JobStatus, ListPartFinderActivityResponse, PartFinderBatch } from '@/api/generatedApi';
import { BaseContextMenu } from '@/components/ContextMenu/BaseContextMenu';
import { CopyMenuItem, getCopyValueFromColumn } from '@/components/ContextMenu/CopyMenuItem';
import { DATA_GRID_LAZY_PAGE_SIZE } from '@/components/DataGrid/definitions';
import { SectionHeading } from '@/components/PartFinder/SectionHeading';
import { PART_FINDER_ACTIVITY_QUERY_KEY } from '@/hooks/api/part-finder-activity/query';
import { useInfiniteQuery } from '@/hooks/api/useInfiniteQuery';
import { useRetryPartFinderReports } from '@/hooks/api/part-finder-reports/useRetryPartFinderReports';
import { useCancelPartFinderReports } from '@/hooks/api/part-finder-reports/useCancelPartFinderReports';
import { usePartFinderExport } from '@/hooks/api/part-finder-reports/usePartFinderExport';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useColumnWidths } from '@/hooks/useColumnWidths';
import { useContextMenu } from '@/hooks/useContextMenu';
import { useToast } from '@/hooks/useToast';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { pluralize } from '@/utils/format';
import {
  BlockOutlined,
  DeleteOutlined,
  DriveFileRenameOutline,
  FileDownloadOutlined,
  HistoryOutlined,
  ReplayOutlined,
} from '@mui/icons-material';
import { Alert, Box } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { DataGridPro, GridRowSelectionModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { useEffect, useMemo, useRef, useState } from 'react';
import { buildActivityColumns } from './activityColumns';
import { UnifiedRow, buildUnifiedRows, isAnyActivityInProgress, isInProgress, isRetryable } from './activityRows';
import { BulkDeleteReportsModal } from './BulkDeleteReportsModal';
import { BulkActionSidebar } from './BulkActionSidebar';
import { CancelBatchModal } from './CancelBatchModal';
import { ContextMenuAction } from './ContextMenuAction';
import { DeleteBatchModal } from './DeleteBatchModal';
import { partFinderReportColumns } from './columnDefinitions';
import { PartFinderZeroState } from './PartFinderZeroState';
import { RenameBatchDialog } from './RenameBatchDialog';
import { resolveContextTargetReportIds } from './reportSelection';
import { useBatchChildren } from './useBatchChildren';

const ACTIVITY_POLL_INTERVAL_MS = 5000;

const TableContainer = styled(Box)({
  display: 'flex',
  flex: 1,
  minHeight: 0,
  flexDirection: 'column',
  width: '100%',
});

const HeadingWrapper = styled(Box)({ marginTop: '32px' });

const DataGridWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  width: '100%',
  '& .MuiDataGrid-root': { borderRadius: '6px !important' },
  '& .MuiDataGrid-columnHeaders': { background: alpha(theme.palette.secondary.main, 0.1) },
  '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 },
  '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
  '& .MuiDataGrid-cell:focus': { outline: 'none !important' },
  '& .batch-child-row': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.05),
    boxShadow: `inset 3px 0 0 0 ${alpha(theme.palette.primary.main, 0.5)}`,
  },
  '& .batch-child-row .MuiDataGrid-cell:first-of-type': { paddingLeft: '44px' },
}));

const GridSlot = styled(Box)({ display: 'flex', flex: 1, minWidth: 0, minHeight: 0 });

export const PartFinderActivityList = () => {
  const { getTenantId, getPath } = useTenants();
  const tenantId = getTenantId();
  const { selfAwareEmail, permissions } = useGetCurrentUser();
  const { isPartFinderThumbnailsEnabled } = useFeatureFlags();
  const canRenameBatch = permissions?.canEditPartFinderReport ?? false;
  const gridApi = useGridApiRef();

  const { showToast } = useToast();
  const { exportToExcel } = usePartFinderExport();
  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([]);
  const [selectedBatchIds, setSelectedBatchIds] = useState<string[]>([]);
  const [reportIdsToDelete, setReportIdsToDelete] = useState<string[]>([]);
  const [batchToRename, setBatchToRename] = useState<PartFinderBatch>();
  const [batchToCancel, setBatchToCancel] = useState<PartFinderBatch>();
  const [batchToDelete, setBatchToDelete] = useState<PartFinderBatch>();

  const activityQuery = useInfiniteQuery<
    { tenantId: string; page?: number; perPage?: number },
    ListPartFinderActivityResponse,
    'activity'
  >({
    queryKey: [PART_FINDER_ACTIVITY_QUERY_KEY, { tenantId }],
    queryFunction: ({ tenantId: forTenant, page, perPage }) =>
      api.tenants.listPartFinderActivity(forTenant, page ?? 1, perPage ?? DATA_GRID_LAZY_PAGE_SIZE),
    queryParams: { tenantId },
    options: { enabled: !!tenantId },
    dataKey: 'activity',
  });

  const entries = activityQuery.activity ?? [];
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = activityQuery;

  const {
    expandedBatchIds,
    loadingBatchIds,
    childrenByBatch,
    childFetchFailed,
    setChildFetchFailed,
    toggleBatch,
    resetChildren,
  } = useBatchChildren(entries, tenantId, gridApi);

  const retryReports = useRetryPartFinderReports({
    onSuccess: () => {
      showToast({ message: 'Retrying reports', type: 'success' });
      resetChildren();
    },
    onError: message => showToast({ message, type: 'error' }),
  });

  const cancelReports = useCancelPartFinderReports({
    onSuccess: () => {
      showToast({ message: 'Cancelled reports', type: 'success' });
      resetChildren();
    },
    onError: message => showToast({ message, type: 'error' }),
  });

  const rows = useMemo<UnifiedRow[]>(() => buildUnifiedRows(entries, childrenByBatch), [entries, childrenByBatch]);

  const reportStatusById = useMemo(() => {
    const map = new Map<string, JobStatus>();
    for (const row of rows) {
      if (row.kind === 'report') map.set(row.report.id, row.report.status);
    }
    return map;
  }, [rows]);

  const reportParentBatchId = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of rows) {
      if (row.kind === 'report' && row.report.batchId) {
        map.set(row.report.id, row.report.batchId);
      }
    }
    return map;
  }, [rows]);

  const dedupedSelectedReportIds = useMemo(() => {
    const selectedBatchIdSet = new Set(selectedBatchIds);
    return selectedReportIds.filter(id => {
      const parentBatchId = reportParentBatchId.get(id);
      return !(parentBatchId && selectedBatchIdSet.has(parentBatchId));
    });
  }, [selectedReportIds, selectedBatchIds, reportParentBatchId]);

  const batchStatusById = useMemo(() => {
    const map = new Map<string, JobStatus>();
    for (const entry of entries) {
      if (entry.type === 'batch') map.set(entry.batch.id, entry.batch.status);
    }
    return map;
  }, [entries]);

  const cancellableBatchIds = useMemo(
    () => selectedBatchIds.filter(id => batchStatusById.get(id) === JobStatus.RUNNING),
    [selectedBatchIds, batchStatusById]
  );

  const cancellableReportIds = useMemo(
    () =>
      dedupedSelectedReportIds.filter(id => {
        const status = reportStatusById.get(id);
        return status !== undefined && isInProgress(status);
      }),
    [dedupedSelectedReportIds, reportStatusById]
  );

  useEffect(() => {
    if (!gridApi.current) return;
    for (const rowId of expandedBatchIds) {
      const node = gridApi.current.getRowNode(rowId);
      if (node?.type === 'group') {
        gridApi.current.setRowChildrenExpansion(rowId, true);
      }
    }
  }, [rows, expandedBatchIds, gridApi]);

  const refetchInProgressPagesReference = useRef(activityQuery.refetchMatchingPages);
  refetchInProgressPagesReference.current = activityQuery.refetchMatchingPages;
  const anyInProgress = isAnyActivityInProgress(entries);
  useEffect(() => {
    if (!anyInProgress) return;
    const interval = setInterval(() => {
      void refetchInProgressPagesReference.current(entry =>
        entry.type === 'batch' ? isInProgress(entry.batch.status) : isInProgress(entry.report.status)
      );
    }, ACTIVITY_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [anyInProgress]);

  const columns = buildActivityColumns({
    expandedBatchIds,
    loadingBatchIds,
    toggleBatch,
    getPath,
    selfAwareEmail,
    thumbnailsEnabled: isPartFinderThumbnailsEnabled,
  });

  const { columns: resizableColumns, onColumnResize, onColumnWidthChange } = useColumnWidths(columns, gridApi);

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    const all = (gridApi.current?.getAllRowIds() as string[] | undefined) ?? [];
    const ids =
      newSelection.type === 'exclude'
        ? all.filter(id => !newSelection.ids.has(id))
        : ([...newSelection.ids] as string[]);
    const reportIds = ids.filter(id => id.startsWith('report-')).map(id => id.replace('report-', ''));
    const batchIds = ids.filter(id => id.startsWith('batch-')).map(id => id.replace('batch-', ''));
    setSelectedReportIds(reportIds);
    setSelectedBatchIds(batchIds);
  };

  const resetSelection = () => {
    setSelectedReportIds([]);
    setSelectedBatchIds([]);
    gridApi.current?.setRowSelectionModel({ type: 'include', ids: new Set() });
    resetChildren();
  };

  const {
    contextMenuPosition,
    contextMenuClickedItem,
    contextMenuClickedField,
    handleGridContextMenu,
    handleCloseContextMenu,
  } = useContextMenu<UnifiedRow>({
    canUseContextMenu: true,
    getRowById: rowId => gridApi.current?.getRow(rowId) ?? undefined,
  });

  const contextMenuReport = contextMenuClickedItem?.kind === 'report' ? contextMenuClickedItem.report : undefined;
  const contextMenuBatch = contextMenuClickedItem?.kind === 'batch' ? contextMenuClickedItem.batch : undefined;
  const canRenameContextBatch = canRenameBatch && !!contextMenuBatch;
  const canModifyContextBatch = canRenameBatch && !!contextMenuBatch;
  const isContextBatchRunning = contextMenuBatch?.status === JobStatus.RUNNING;
  const contextTargetReportIds = resolveContextTargetReportIds(contextMenuReport, selectedReportIds);
  const copyValue = getCopyValueFromColumn(partFinderReportColumns, contextMenuReport, contextMenuClickedField);
  const hasCopyValue = !!copyValue && contextTargetReportIds.length === 1;
  const deleteLabel = contextTargetReportIds.length > 1 ? `Delete ${contextTargetReportIds.length} reports` : 'Delete';
  const exportLabel =
    contextTargetReportIds.length > 1
      ? `Export ${pluralize(contextTargetReportIds.length, 'report', 'reports')} to Excel`
      : 'Export to Excel';

  const retryTargetReportIds = contextTargetReportIds.filter(id => {
    const status = reportStatusById.get(id);
    return status !== undefined && isRetryable(status);
  });
  const retryLabel =
    retryTargetReportIds.length > 1 ? `Retry ${pluralize(retryTargetReportIds.length, 'report', 'reports')}` : 'Retry';

  const cancelTargetReportIds = contextTargetReportIds.filter(id => {
    const status = reportStatusById.get(id);
    return status !== undefined && isInProgress(status);
  });
  const cancelLabel =
    cancelTargetReportIds.length > 1
      ? `Cancel ${pluralize(cancelTargetReportIds.length, 'report', 'reports')}`
      : 'Cancel';

  const handleDeleteFromContextMenu = () => {
    if (contextTargetReportIds.length === 0) return;
    handleCloseContextMenu();
    setReportIdsToDelete(contextTargetReportIds);
  };

  const handleExportReportsFromContextMenu = () => {
    if (contextTargetReportIds.length === 0) return;
    handleCloseContextMenu();
    void exportToExcel({ reportIds: contextTargetReportIds });
  };

  const handleExportBatchFromContextMenu = () => {
    if (!contextMenuBatch) return;
    handleCloseContextMenu();
    void exportToExcel({ batchIds: [contextMenuBatch.id] });
  };

  const handleRetryFromContextMenu = () => {
    if (retryTargetReportIds.length === 0) return;
    handleCloseContextMenu();
    retryReports.mutate(retryTargetReportIds);
  };

  const handleCancelReportFromContextMenu = () => {
    if (cancelTargetReportIds.length === 0) return;
    handleCloseContextMenu();
    cancelReports.mutate(cancelTargetReportIds);
  };

  const handleRenameFromContextMenu = () => {
    if (!contextMenuBatch) return;
    handleCloseContextMenu();
    setBatchToRename(contextMenuBatch);
  };

  const handleCancelBatchFromContextMenu = () => {
    if (!contextMenuBatch) return;
    handleCloseContextMenu();
    setBatchToCancel(contextMenuBatch);
  };

  const handleDeleteBatchFromContextMenu = () => {
    if (!contextMenuBatch) return;
    handleCloseContextMenu();
    setBatchToDelete(contextMenuBatch);
  };

  const handleDeleteFromContextComplete = () => {
    const wasBulk = reportIdsToDelete.length > 1;
    setReportIdsToDelete([]);
    if (wasBulk) resetSelection();
    else resetChildren();
  };

  const handleRowsScrollEnd = () => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  };

  const isEmpty = !activityQuery.isLoading && entries.length === 0;
  if (isEmpty) {
    return <PartFinderZeroState />;
  }

  return (
    <TableContainer>
      <HeadingWrapper>
        <SectionHeading variant='page' icon={<HistoryOutlined />} title='Previous Searches' />
      </HeadingWrapper>
      {childFetchFailed && (
        <Alert severity='error' onClose={() => setChildFetchFailed(false)} sx={{ marginTop: '8px' }}>
          Couldn&apos;t load the parts for that batch. Expand it again to retry.
        </Alert>
      )}
      <DataGridWrapper>
        <GridSlot>
          <DataGridPro<UnifiedRow>
            apiRef={gridApi}
            sx={{ height: '100%', width: '100%' }}
            rows={rows}
            columns={resizableColumns}
            onColumnResize={onColumnResize}
            onColumnWidthChange={onColumnWidthChange}
            treeData
            getTreeDataPath={row => row.path}
            defaultGroupingExpansionDepth={0}
            getRowId={row => row.id}
            getRowClassName={parameters => (parameters.row.path.length > 1 ? 'batch-child-row' : '')}
            rowHeight={80}
            columnHeaderHeight={40}
            density='compact'
            checkboxSelection
            onRowSelectionModelChange={handleSelectionChange}
            disableRowSelectionOnClick
            disableColumnSorting
            onRowsScrollEnd={handleRowsScrollEnd}
            scrollEndThreshold={200}
            loading={activityQuery.isLoading || isFetchingNextPage}
            hideFooter
            initialState={{
              pinnedColumns: { right: ['__check__'] },
              columns: { columnVisibilityModel: { __tree_data_group__: false } },
            }}
            slotProps={{ row: { onContextMenu: handleGridContextMenu } }}
          />
        </GridSlot>
        <BulkActionSidebar
          selectedReportIds={dedupedSelectedReportIds}
          selectedBatchIds={selectedBatchIds}
          cancellableReportIds={cancellableReportIds}
          cancellableBatchIds={cancellableBatchIds}
          onDelete={resetSelection}
          onCancel={resetSelection}
        />
      </DataGridWrapper>
      {(hasCopyValue || contextMenuReport || contextMenuBatch) && (
        <BaseContextMenu anchorPosition={contextMenuPosition} onClose={handleCloseContextMenu}>
          {hasCopyValue && contextMenuReport && (
            <CopyMenuItem
              columns={partFinderReportColumns}
              row={contextMenuReport}
              field={contextMenuClickedField}
              onCopy={handleCloseContextMenu}
            />
          )}
          {contextMenuReport && (
            <ContextMenuAction
              icon={<FileDownloadOutlined fontSize='small' />}
              label={exportLabel}
              onClick={handleExportReportsFromContextMenu}
            />
          )}
          {contextMenuBatch && (
            <ContextMenuAction
              icon={<FileDownloadOutlined fontSize='small' />}
              label='Export to Excel'
              onClick={handleExportBatchFromContextMenu}
            />
          )}
          {canRenameContextBatch && (
            <ContextMenuAction
              icon={<DriveFileRenameOutline fontSize='small' />}
              label='Rename'
              onClick={handleRenameFromContextMenu}
            />
          )}
          {canModifyContextBatch && isContextBatchRunning && (
            <ContextMenuAction
              icon={<BlockOutlined fontSize='small' />}
              label='Cancel batch'
              onClick={handleCancelBatchFromContextMenu}
            />
          )}
          {canModifyContextBatch && (
            <ContextMenuAction
              icon={<DeleteOutlined fontSize='small' color='error' />}
              label='Delete batch'
              onClick={handleDeleteBatchFromContextMenu}
            />
          )}
          {contextMenuReport && retryTargetReportIds.length > 0 && (
            <ContextMenuAction
              icon={<ReplayOutlined fontSize='small' />}
              label={retryLabel}
              onClick={handleRetryFromContextMenu}
            />
          )}
          {contextMenuReport && cancelTargetReportIds.length > 0 && (
            <ContextMenuAction
              icon={<BlockOutlined fontSize='small' />}
              label={cancelLabel}
              onClick={handleCancelReportFromContextMenu}
            />
          )}
          {contextMenuReport && (
            <ContextMenuAction
              icon={<DeleteOutlined fontSize='small' color='error' />}
              label={deleteLabel}
              onClick={handleDeleteFromContextMenu}
            />
          )}
        </BaseContextMenu>
      )}
      <BulkDeleteReportsModal
        open={reportIdsToDelete.length > 0}
        onClose={() => setReportIdsToDelete([])}
        selectedReportIds={reportIdsToDelete}
        onDeleteComplete={handleDeleteFromContextComplete}
      />
      {batchToRename && <RenameBatchDialog batch={batchToRename} open onClose={() => setBatchToRename(undefined)} />}
      {batchToCancel && (
        <CancelBatchModal
          batch={batchToCancel}
          open
          onClose={() => setBatchToCancel(undefined)}
          onCancelled={resetChildren}
        />
      )}
      {batchToDelete && (
        <DeleteBatchModal
          batch={batchToDelete}
          open
          onClose={() => setBatchToDelete(undefined)}
          onDeleted={resetSelection}
        />
      )}
    </TableContainer>
  );
};
